import { computed, Injectable, signal } from '@angular/core';
import { finalize, Observable, throwError } from 'rxjs';

export type LoadingDomain = 'boot' | 'content' | 'action' | 'background';

@Injectable({
  providedIn: 'root',
})
export class GlobalLoadingService {
  private counters = signal<Record<LoadingDomain, number>>({
    boot: 0,
    content: 0,
    action: 0,
    background: 0,
  });

  readonly isLoading = computed(() =>
    Object.entries(this.counters()).some(([domain, count]) => count > 0 && domain !== 'background')
  );

  start(domain: LoadingDomain): void {
    this.counters.update((c) => ({ ...c, [domain]: c[domain] + 1 }));
  }

  stop(domain: LoadingDomain): void {
    this.counters.update((c) => ({ ...c, [domain]: Math.max(0, c[domain] - 1) }));
  }

  track<T>(domain: LoadingDomain, source$: Observable<T>): Observable<T> {
    this.start(domain);
    return source$.pipe(finalize(() => this.stop(domain)));
  }

  guardAndTrack<T>(
    condition: boolean,
    domain: LoadingDomain,
    source$: () => Observable<T>,
    errorMessage = 'Invalid condition'
  ): Observable<T> {
    if (!condition) {
      return throwError(() => errorMessage);
    }

    return this.track(domain, source$());
  }
}
